import { useState, useEffect } from 'react';
import { Users, BarChart3, Radio, Settings, RefreshCw, Trash2, Shield, ShieldOff, Search, TrendingUp, Mail, Send, Key, Copy, Check, RotateCcw, Plug, Eye, EyeOff, Sparkles, Megaphone, Plus, Edit3, X } from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import toast from 'react-hot-toast';
import { adminAnnouncements, Announcement } from '../lib/api';

type Tab = 'stats' | 'users' | 'feeds' | 'analytics' | 'integrations' | 'licenses' | 'announcements' | 'config';

interface Stats {
  totalUsers: number;
  activeUsers: number;
  totalFeeds: number;
  totalArticles: number;
  totalSubscriptions: number;
  totalShares: number;
  totalComments: number;
  brokenFeeds: number;
  popularFeeds: Array<{
    id: string;
    title: string;
    url: string;
    _count: { subscriptions: number };
  }>;
  recentUsers: Array<{
    id: string;
    username: string;
    displayName: string;
    createdAt: string;
  }>;
}

interface User {
  id: string;
  username: string;
  email: string;
  displayName: string | null;
  avatar: string | null;
  role: string;
  isAdmin: boolean;
  createdAt: string;
  firstLoginAt: string | null;
  _count: {
    subscriptions: number;
    followers: number;
    following: number;
    shares: number;
    comments: number;
  };
}

interface Feed {
  id: string;
  url: string;
  title: string;
  description: string | null;
  siteUrl: string | null;
  lastFetchedAt: string | null;
  lastSuccessAt: string | null;
  fetchError: string | null;
  createdAt: string;
  _count: {
    subscriptions: number;
    articles: number;
  };
}

interface InstanceConfig {
  id: string;
  name: string;
  description: string | null;
  allowRegistration: boolean;
  maxUsers: number;
  createdAt: string;
  updatedAt: string;
}

interface AnalyticsInsights {
  umami?: {
    pageviews: number;
    uniqueVisitors: number;
    averageVisitTime: number;
    bounceRate: number;
    topPages: Array<{ path: string; views: number }>;
    topEvents: Array<{ event: string; count: number }>;
  };
  instance: {
    totalUsers: number;
    activeUsers7d: number;
    activeUsers30d: number;
    totalFeeds: number;
    brokenFeeds: number;
    totalArticles: number;
  };
  engagement: {
    totalShares: number;
    sharesLastWeek: number;
    totalComments: number;
    commentsLastWeek: number;
  };
  content: {
    popularFeeds: Array<{ title: string; subscribers: number }>;
    weakSignals: Array<{ title: string; subscribers: number }>;
    brokenFeeds: Array<{ title: string; url: string; error: string }>;
  };
  growth: {
    newUsersLastWeek: number;
    newUsersLastMonth: number;
    retentionRate7d: number;
  };
}

interface License {
  id: string;
  licenseKey: string;
  customerEmail: string;
  customerName: string | null;
  status: string;
  machineId: string | null;
  instanceUrl: string | null;
  activatedAt: string | null;
  lastSeenAt: string | null;
  lastSeenIp: string | null;
  appVersion: string | null;
  createdAt: string;
}

interface IntegrationStatus {
  openai: {
    configured: boolean;
    digestEnabled: boolean;
    digestSchedule: string | null;
  };
  email: {
    configured: boolean;
    fromEmail: string | null;
    fromName: string | null;
  };
  analytics: {
    configured: boolean;
    websiteId: string | null;
    scriptUrl: string | null;
  };
}

export default function AdminPanel() {
  const token = useAuthStore((state) => state.token)!;
  const currentUser = useAuthStore((state) => state.user);
  const [activeTab, setActiveTab] = useState<Tab>('stats');
  const [loading, setLoading] = useState(true);

  // Stats
  const [stats, setStats] = useState<Stats | null>(null);

  // Users
  const [users, setUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);

  // Feeds
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [feedFilter, setFeedFilter] = useState<'all' | 'healthy' | 'broken'>('all');
  const [feedPage, setFeedPage] = useState(1);
  const [feedTotal, setFeedTotal] = useState(0);

  // Config
  const [config, setConfig] = useState<InstanceConfig | null>(null);
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({
    name: '',
    description: '',
    allowRegistration: true,
    maxUsers: 0,
  });

  // Analytics
  const [analytics, setAnalytics] = useState<AnalyticsInsights | null>(null);


  // Licenses
  const [licenses, setLicenses] = useState<License[]>([]);
  const [newLicenseEmail, setNewLicenseEmail] = useState('');
  const [newLicenseName, setNewLicenseName] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Integrations
  const [integrations, setIntegrations] = useState<IntegrationStatus | null>(null);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showResendKey, setShowResendKey] = useState(false);
  const [openaiForm, setOpenaiForm] = useState({ apiKey: '', digestEnabled: false, digestSchedule: '0 12 * * 0' });
  const [emailForm, setEmailForm] = useState({ apiKey: '', fromEmail: '', fromName: '' });
  const [analyticsForm, setAnalyticsForm] = useState({ enabled: false, websiteId: '', scriptUrl: '' });
  const [testingOpenAI, setTestingOpenAI] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  // Announcements
  const [announcementsList, setAnnouncementsList] = useState<Announcement[]>([]);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    type: 'modal' as 'modal' | 'notification',
    priority: 0,
    isActive: true,
    expiresAt: '',
    targetRole: '' as '' | 'user' | 'admin',
  });

  useEffect(() => {
    loadTabData();
  }, [activeTab, userPage, userSearch, feedPage, feedFilter]);

  async function loadTabData() {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'stats':
          await loadStats();
          break;
        case 'users':
          await loadUsers();
          break;
        case 'feeds':
          await loadFeeds();
          break;
        case 'analytics':
          await loadAnalytics();
          break;
        case 'integrations':
          await loadIntegrations();
          break;
        case 'licenses':
          await loadLicenses();
          break;
        case 'announcements':
          await loadAnnouncements();
          break;
        case 'config':
          await loadConfig();
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    const response = await fetch('/api/admin/stats', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to load stats');
    const data = await response.json();
    setStats(data);
  }

  async function loadUsers() {
    const params = new URLSearchParams({
      page: userPage.toString(),
      limit: '20',
      ...(userSearch && { search: userSearch }),
    });

    const response = await fetch(`/api/admin/users?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to load users');
    const data = await response.json();
    setUsers(data.users);
    setUserTotal(data.pagination.total);
  }

  async function loadFeeds() {
    const params = new URLSearchParams({
      page: feedPage.toString(),
      limit: '50',
      status: feedFilter,
    });

    const response = await fetch(`/api/admin/feeds?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to load feeds');
    const data = await response.json();
    setFeeds(data.feeds);
    setFeedTotal(data.pagination.total);
  }

  async function loadConfig() {
    const response = await fetch('/api/admin/config', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to load config');
    const data = await response.json();
    setConfig(data);
    setConfigForm({
      name: data.name,
      description: data.description || '',
      allowRegistration: data.allowRegistration,
      maxUsers: data.maxUsers,
    });
  }

  async function loadAnalytics() {
    const insightsResponse = await fetch('/api/admin/analytics/insights', {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (insightsResponse.ok) {
      const data = await insightsResponse.json();
      setAnalytics(data);
    }
  }

  async function loadLicenses() {
    const response = await fetch('/api/license/admin/list', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to load licenses');
    const data = await response.json();
    setLicenses(data.licenses);
  }

  async function loadIntegrations() {
    const response = await fetch('/api/admin/integrations', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to load integrations');
    const data = await response.json();
    setIntegrations(data);
    // Update forms with current values (without showing API keys)
    setOpenaiForm({
      apiKey: '',
      digestEnabled: data.openai.digestEnabled,
      digestSchedule: data.openai.digestSchedule || '0 12 * * 0',
    });
    setEmailForm({
      apiKey: '',
      fromEmail: data.email.fromEmail || '',
      fromName: data.email.fromName || '',
    });
    setAnalyticsForm({
      enabled: data.analytics.configured,
      websiteId: data.analytics.websiteId || '',
      scriptUrl: data.analytics.scriptUrl || '',
    });
  }

  async function loadAnnouncements() {
    const data = await adminAnnouncements.getAll(token);
    setAnnouncementsList(data.announcements);
  }

  function resetAnnouncementForm() {
    setAnnouncementForm({
      title: '',
      content: '',
      type: 'modal',
      priority: 0,
      isActive: true,
      expiresAt: '',
      targetRole: '',
    });
    setEditingAnnouncement(null);
    setShowAnnouncementForm(false);
  }

  function startEditAnnouncement(announcement: Announcement) {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      priority: announcement.priority,
      isActive: announcement.isActive,
      expiresAt: announcement.expiresAt ? announcement.expiresAt.split('T')[0] : '',
      targetRole: (announcement.targetRole as '' | 'user' | 'admin') || '',
    });
    setShowAnnouncementForm(true);
  }

  async function saveAnnouncement() {
    try {
      const data = {
        title: announcementForm.title,
        content: announcementForm.content,
        type: announcementForm.type,
        priority: announcementForm.priority,
        isActive: announcementForm.isActive,
        expiresAt: announcementForm.expiresAt ? new Date(announcementForm.expiresAt).toISOString() : null,
        targetRole: announcementForm.targetRole || null,
      };

      if (editingAnnouncement) {
        await adminAnnouncements.update(token, editingAnnouncement.id, data);
        toast.success('Announcement updated');
      } else {
        await adminAnnouncements.create(token, data);
        toast.success('Announcement created');
      }

      resetAnnouncementForm();
      await loadAnnouncements();
    } catch (error) {
      toast.error(editingAnnouncement ? 'Failed to update announcement' : 'Failed to create announcement');
    }
  }

  async function deleteAnnouncement(id: string) {
    if (!confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await adminAnnouncements.delete(token, id);
      toast.success('Announcement deleted');
      await loadAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  }

  async function toggleAnnouncementActive(announcement: Announcement) {
    try {
      await adminAnnouncements.update(token, announcement.id, { isActive: !announcement.isActive });
      toast.success(announcement.isActive ? 'Announcement deactivated' : 'Announcement activated');
      await loadAnnouncements();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  }

  async function saveOpenAISettings() {
    try {
      const body: any = {
        digestEnabled: openaiForm.digestEnabled,
        digestSchedule: openaiForm.digestSchedule,
      };
      if (openaiForm.apiKey) {
        body.apiKey = openaiForm.apiKey;
      }

      const response = await fetch('/api/admin/integrations/openai', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to save OpenAI settings');
      toast.success('OpenAI settings saved');
      setOpenaiForm(prev => ({ ...prev, apiKey: '' }));
      await loadIntegrations();
    } catch (error) {
      toast.error('Failed to save OpenAI settings');
    }
  }

  async function testOpenAIKey() {
    setTestingOpenAI(true);
    try {
      const response = await fetch('/api/admin/integrations/openai/test', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.error || 'API key test failed');
      }
    } catch (error) {
      toast.error('Failed to test API key');
    } finally {
      setTestingOpenAI(false);
    }
  }

  async function saveEmailSettings() {
    try {
      const body: any = {
        fromEmail: emailForm.fromEmail,
        fromName: emailForm.fromName,
      };
      if (emailForm.apiKey) {
        body.apiKey = emailForm.apiKey;
      }

      const response = await fetch('/api/admin/integrations/email', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to save email settings');
      toast.success('Email settings saved');
      setEmailForm(prev => ({ ...prev, apiKey: '' }));
      await loadIntegrations();
    } catch (error) {
      toast.error('Failed to save email settings');
    }
  }

  async function sendIntegrationTestEmail() {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    try {
      const response = await fetch('/api/admin/integrations/email/test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: testEmailAddress }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setTestEmailAddress('');
      } else {
        toast.error(data.error || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    }
  }

  async function saveAnalyticsSettings() {
    try {
      const response = await fetch('/api/admin/integrations/analytics', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: analyticsForm.enabled,
          websiteId: analyticsForm.websiteId,
          scriptUrl: analyticsForm.scriptUrl,
        }),
      });

      if (!response.ok) throw new Error('Failed to save analytics settings');
      toast.success('Analytics settings saved');
      await loadIntegrations();
    } catch (error) {
      toast.error('Failed to save analytics settings');
    }
  }

  async function issueLicense() {
    if (!newLicenseEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      const response = await fetch('/api/license/admin/issue', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerEmail: newLicenseEmail,
          customerName: newLicenseName || undefined,
        }),
      });

      if (!response.ok) throw new Error('Failed to issue license');

      const data = await response.json();
      toast.success(`License issued: ${data.license.licenseKey}`);
      setNewLicenseEmail('');
      setNewLicenseName('');
      await loadLicenses();
    } catch (error) {
      toast.error('Failed to issue license');
    }
  }

  async function revokeLicense(id: string) {
    if (!confirm('Are you sure you want to revoke this license?')) return;

    try {
      const response = await fetch(`/api/license/admin/revoke/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to revoke license');

      toast.success('License revoked');
      await loadLicenses();
    } catch (error) {
      toast.error('Failed to revoke license');
    }
  }

  async function reinstateLicense(id: string) {
    try {
      const response = await fetch(`/api/license/admin/reinstate/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to reinstate license');

      toast.success('License reinstated');
      await loadLicenses();
    } catch (error) {
      toast.error('Failed to reinstate license');
    }
  }

  async function resetActivation(id: string) {
    if (!confirm('Reset activation? The customer will need to reactivate.')) return;

    try {
      const response = await fetch(`/api/license/admin/reset-activation/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to reset activation');

      toast.success('Activation reset');
      await loadLicenses();
    } catch (error) {
      toast.error('Failed to reset activation');
    }
  }

  function copyLicenseKey(key: string) {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    toast.success('License key copied');
    setTimeout(() => setCopiedKey(null), 2000);
  }

  async function toggleUserRole(userId: string, currentRole: string) {
    try {
      const newRole = currentRole === 'admin' ? 'user' : 'admin';
      const newIsAdmin = newRole === 'admin';

      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole, isAdmin: newIsAdmin }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update user');
      }

      toast.success(`User role updated to ${newRole}`);
      await loadUsers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update user role');
    }
  }

  async function deleteUser(userId: string, username: string) {
    if (!confirm(`Are you sure you want to delete user "${username}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete user');
      }

      toast.success(`User "${username}" deleted`);
      await loadUsers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete user');
    }
  }

  async function refreshFeed(feedId: string, feedTitle: string) {
    try {
      const response = await fetch(`/api/admin/feeds/${feedId}/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to refresh feed');

      toast.success(`Feed "${feedTitle}" refresh queued`);
      await loadFeeds();
    } catch (error) {
      toast.error('Failed to refresh feed');
    }
  }

  async function deleteFeed(feedId: string, feedTitle: string) {
    if (!confirm(`Are you sure you want to delete feed "${feedTitle}"? This will delete all articles and subscriptions. This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/feeds/${feedId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete feed');

      toast.success(`Feed "${feedTitle}" deleted`);
      await loadFeeds();
    } catch (error) {
      toast.error('Failed to delete feed');
    }
  }

  async function saveConfig() {
    try {
      const response = await fetch('/api/admin/config', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(configForm),
      });

      if (!response.ok) throw new Error('Failed to save config');

      const data = await response.json();
      setConfig(data);
      setEditingConfig(false);
      toast.success('Configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  }

  return (
    <div className="flex flex-col h-screen bg-canvas">
      {/* Header */}
      <header className="bg-canvas px-6 pt-6 pb-4">
        <div className="kicker mb-1">Admin</div>
        <h1 className="text-2xl">Instance controls.</h1>
      </header>

      {/* Tabs */}
      <div className="bg-canvas">
        <nav className="rail">
          <button
            onClick={() => setActiveTab('stats')}
            className={`rail-tab ${activeTab === 'stats' ? 'is-active' : ''}`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Statistics
            </div>
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`rail-tab ${activeTab === 'users' ? 'is-active' : ''}`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </div>
          </button>
          <button
            onClick={() => setActiveTab('feeds')}
            className={`rail-tab ${activeTab === 'feeds' ? 'is-active' : ''}`}
          >
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4" />
              Feeds
            </div>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`rail-tab ${activeTab === 'analytics' ? 'is-active' : ''}`}
          >
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Analytics
            </div>
          </button>
          <button
            onClick={() => setActiveTab('integrations')}
            className={`rail-tab ${activeTab === 'integrations' ? 'is-active' : ''}`}
          >
            <div className="flex items-center gap-2">
              <Plug className="w-4 h-4" />
              Integrations
            </div>
          </button>
          <button
            onClick={() => setActiveTab('licenses')}
            className={`rail-tab ${activeTab === 'licenses' ? 'is-active' : ''}`}
          >
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              Licenses
            </div>
          </button>
          <button
            onClick={() => setActiveTab('announcements')}
            className={`rail-tab ${activeTab === 'announcements' ? 'is-active' : ''}`}
          >
            <div className="flex items-center gap-2">
              <Megaphone className="w-4 h-4" />
              Announcements
            </div>
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`rail-tab ${activeTab === 'config' ? 'is-active' : ''}`}
          >
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configuration
            </div>
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-ink-muted">Loading...</div>
          </div>
        ) : (
          <>
            {/* Statistics Tab */}
            {activeTab === 'stats' && stats && (
              <div className="space-y-6">
                {/* Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-canvas p-6 border border-edge">
                    <div className="text-sm font-medium text-ink-muted">Total Users</div>
                    <div className="text-3xl font-bold text-ink mt-2">{stats.totalUsers}</div>
                    <div className="text-xs text-ink-muted mt-1">{stats.activeUsers} active (7 days)</div>
                  </div>
                  <div className="bg-canvas p-6 border border-edge">
                    <div className="text-sm font-medium text-ink-muted">Total Feeds</div>
                    <div className="text-3xl font-bold text-ink mt-2">{stats.totalFeeds}</div>
                    <div className="text-xs text-signal mt-1">{stats.brokenFeeds} with errors</div>
                  </div>
                  <div className="bg-canvas p-6 border border-edge">
                    <div className="text-sm font-medium text-ink-muted">Total Articles</div>
                    <div className="text-3xl font-bold text-ink mt-2">{stats.totalArticles.toLocaleString()}</div>
                    <div className="text-xs text-ink-muted mt-1">{stats.totalSubscriptions} subscriptions</div>
                  </div>
                  <div className="bg-canvas p-6 border border-edge">
                    <div className="text-sm font-medium text-ink-muted">Engagement</div>
                    <div className="text-3xl font-bold text-ink mt-2">{stats.totalShares + stats.totalComments}</div>
                    <div className="text-xs text-ink-muted mt-1">{stats.totalShares} shares, {stats.totalComments} comments</div>
                  </div>
                </div>

                {/* Popular Feeds */}
                <div className="bg-canvas border border-edge p-6">
                  <h2 className="text-lg font-semibold mb-4">Popular Feeds</h2>
                  <div className="space-y-3">
                    {stats.popularFeeds.map((feed) => (
                      <div key={feed.id} className="flex items-center justify-between py-2 border-b border-edge-subtle last:border-0">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-ink truncate">{feed.title}</div>
                          <div className="text-xs text-ink-muted truncate">{feed.url}</div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <span className="kicker text-ink">
                            {feed._count.subscriptions} subscriber{feed._count.subscriptions !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recently Active Users */}
                <div className="bg-canvas border border-edge p-6">
                  <h2 className="text-lg font-semibold mb-4">Recently Active Users</h2>
                  <div className="space-y-3">
                    {stats.recentUsers.map((user) => (
                      <div key={user.id} className="flex items-center justify-between py-2 border-b border-edge-subtle last:border-0">
                        <div>
                          <div className="font-medium text-ink">{user.displayName || user.username}</div>
                          <div className="text-xs text-ink-muted">@{user.username}</div>
                        </div>
                        <div className="text-sm text-ink-muted">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {/* Search */}
                <div className="bg-canvas border border-edge p-4">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-ink-muted" />
                    <input
                      type="text"
                      placeholder="Search users by username, email, or display name..."
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setUserPage(1);
                      }}
                      className="flex-1 outline-none text-sm"
                    />
                  </div>
                </div>

                {/* Users Table */}
                <div className="bg-canvas border border-edge overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-edge-subtle">
                      <thead className="bg-surface-sunken">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Role</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Activity</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Joined</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-ink-muted uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-canvas divide-y divide-edge-subtle">
                        {users.map((user) => (
                          <tr key={user.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div>
                                  <div className="text-sm font-medium text-ink">{user.displayName || user.username}</div>
                                  <div className="text-xs text-ink-muted">@{user.username}</div>
                                  <div className="text-xs text-ink-muted">{user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`kicker ${user.isAdmin ? 'text-ink' : ''}`}>
                                {user.role}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                              <div>{user._count.subscriptions} feeds</div>
                              <div className="text-xs text-ink-muted">{user._count.shares} shares, {user._count.comments} comments</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                              {new Date(user.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                              {user.id !== currentUser?.id && (
                                <>
                                  <button
                                    onClick={() => toggleUserRole(user.id, user.role)}
                                    className="kicker hover:text-ink"
                                    title={user.isAdmin ? 'Remove admin' : 'Make admin'}
                                  >
                                    {user.isAdmin ? <ShieldOff className="w-4 h-4 inline" /> : <Shield className="w-4 h-4 inline" />}
                                  </button>
                                  <button
                                    onClick={() => deleteUser(user.id, user.username)}
                                    className="kicker text-signal hover:text-ink"
                                    title="Delete user"
                                  >
                                    <Trash2 className="w-4 h-4 inline" />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {userTotal > 20 && (
                    <div className="bg-surface-sunken px-6 py-3 flex items-center justify-between border-t border-edge">
                      <div className="text-sm text-ink-secondary">
                        Showing {(userPage - 1) * 20 + 1} to {Math.min(userPage * 20, userTotal)} of {userTotal} users
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setUserPage(p => Math.max(1, p - 1))}
                          disabled={userPage === 1}
                          className="btn min-h-0 py-1"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setUserPage(p => p + 1)}
                          disabled={userPage * 20 >= userTotal}
                          className="btn min-h-0 py-1"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Feeds Tab */}
            {activeTab === 'feeds' && (
              <div className="space-y-4">
                {/* Filter */}
                <div className="bg-canvas border border-edge p-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setFeedFilter('all'); setFeedPage(1); }}
                      className={`btn min-h-0 py-1 ${feedFilter === 'all' ? 'btn-ink' : ''}`}
                    >
                      All Feeds
                    </button>
                    <button
                      onClick={() => { setFeedFilter('healthy'); setFeedPage(1); }}
                      className={`btn min-h-0 py-1 ${feedFilter === 'healthy' ? 'btn-ink' : ''}`}
                    >
                      Healthy
                    </button>
                    <button
                      onClick={() => { setFeedFilter('broken'); setFeedPage(1); }}
                      className={`btn min-h-0 py-1 ${feedFilter === 'broken' ? 'btn-ink' : ''}`}
                    >
                      Broken
                    </button>
                  </div>
                </div>

                {/* Feeds Table */}
                <div className="bg-canvas border border-edge overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-edge-subtle">
                      <thead className="bg-surface-sunken">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Feed</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Stats</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Last Fetched</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-ink-muted uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-canvas divide-y divide-edge-subtle">
                        {feeds.map((feed) => (
                          <tr key={feed.id}>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-ink">{feed.title}</div>
                              <div className="text-xs text-ink-muted truncate max-w-md">{feed.url}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {feed.fetchError ? (
                                <span className="kicker text-signal">
                                  Error
                                </span>
                              ) : (
                                <span className="kicker text-pine">
                                  Healthy
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                              <div>{feed._count.subscriptions} subscribers</div>
                              <div className="text-xs text-ink-muted">{feed._count.articles} articles</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                              {feed.lastFetchedAt ? new Date(feed.lastFetchedAt).toLocaleString() : 'Never'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                              <button
                                onClick={() => refreshFeed(feed.id, feed.title)}
                                className="kicker hover:text-ink"
                                title="Refresh feed"
                              >
                                <RefreshCw className="w-4 h-4 inline" />
                              </button>
                              <button
                                onClick={() => deleteFeed(feed.id, feed.title)}
                                className="kicker text-signal hover:text-ink"
                                title="Delete feed"
                              >
                                <Trash2 className="w-4 h-4 inline" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {feedTotal > 50 && (
                    <div className="bg-surface-sunken px-6 py-3 flex items-center justify-between border-t border-edge">
                      <div className="text-sm text-ink-secondary">
                        Showing {(feedPage - 1) * 50 + 1} to {Math.min(feedPage * 50, feedTotal)} of {feedTotal} feeds
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setFeedPage(p => Math.max(1, p - 1))}
                          disabled={feedPage === 1}
                          className="btn min-h-0 py-1"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setFeedPage(p => p + 1)}
                          disabled={feedPage * 50 >= feedTotal}
                          className="btn min-h-0 py-1"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                {/* Umami Analytics Dashboard */}
                <div className="bg-canvas border border-edge p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">Umami Analytics Dashboard</h2>
                      <p className="text-sm text-ink-muted mt-1">
                        Access the full Umami analytics dashboard below.
                        <span className="font-medium"> Default login: admin / umami</span> (change password after first login!)
                        <br />
                        <span className="text-xs">To set light mode: Profile → Settings → Theme → Light</span>
                      </p>
                    </div>
                    <a
                      href="/umami"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-ink"
                    >
                      Open in New Tab
                    </a>
                  </div>
                </div>
                <div className="bg-canvas border border-edge overflow-hidden" style={{ height: 'calc(100vh - 280px)', colorScheme: 'light' }}>
                  <iframe
                    src="/umami"
                    className="w-full h-full border-0"
                    title="Umami Analytics"
                    style={{ colorScheme: 'light' }}
                  />
                </div>

                {/* Analytics Insights */}
                {analytics && (
                  <>
                    {/* Instance Health */}
                    <div className="bg-canvas border border-edge p-6">
                      <h2 className="text-lg font-semibold mb-4">Instance Health</h2>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-sm text-ink-muted">Total Users</div>
                          <div className="text-2xl font-bold text-ink">{analytics.instance.totalUsers}</div>
                          <div className="text-xs text-ink-muted mt-1">
                            {analytics.instance.activeUsers7d} active (7d), {analytics.instance.activeUsers30d} active (30d)
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-ink-muted">Feeds</div>
                          <div className="text-2xl font-bold text-ink">{analytics.instance.totalFeeds}</div>
                          <div className="text-xs text-signal mt-1">
                            {analytics.instance.brokenFeeds} broken
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-ink-muted">Articles</div>
                          <div className="text-2xl font-bold text-ink">{analytics.instance.totalArticles.toLocaleString()}</div>
                        </div>
                      </div>
                    </div>

                    {/* Engagement */}
                    <div className="bg-canvas border border-edge p-6">
                      <h2 className="text-lg font-semibold mb-4">Engagement</h2>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-sm text-ink-muted">Shares</div>
                          <div className="text-2xl font-bold text-ink">{analytics.engagement.totalShares}</div>
                          <div className="text-xs text-ink-muted mt-1">
                            {analytics.engagement.sharesLastWeek} last week
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-ink-muted">Comments</div>
                          <div className="text-2xl font-bold text-ink">{analytics.engagement.totalComments}</div>
                          <div className="text-xs text-ink-muted mt-1">
                            {analytics.engagement.commentsLastWeek} last week
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-ink-muted">Growth</div>
                          <div className="text-2xl font-bold text-ink">{analytics.growth.newUsersLastWeek}</div>
                          <div className="text-xs text-ink-muted mt-1">
                            new users last week
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Content Insights */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Popular Feeds */}
                      <div className="bg-canvas border border-edge p-6">
                        <h3 className="text-base font-semibold mb-3">Popular Feeds</h3>
                        <div className="space-y-2">
                          {analytics.content.popularFeeds.slice(0, 5).map((feed, i) => (
                            <div key={i} className="flex justify-between items-center text-sm">
                              <span className="text-ink truncate">{feed.title}</span>
                              <span className="text-ink-muted ml-2">{feed.subscribers} subs</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Weak Signals */}
                      <div className="bg-canvas border border-edge p-6">
                        <h3 className="text-base font-semibold mb-3">Weak Signals</h3>
                        <div className="space-y-2">
                          {analytics.content.weakSignals.slice(0, 5).map((feed, i) => (
                            <div key={i} className="flex justify-between items-center text-sm">
                              <span className="text-ink truncate">{feed.title}</span>
                              <span className="text-amber-600 ml-2">{feed.subscribers} subs</span>
                            </div>
                          ))}
                          {analytics.content.weakSignals.length === 0 && (
                            <div className="text-sm text-ink-muted">No weak signals found</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Broken Feeds */}
                    {analytics.content.brokenFeeds.length > 0 && (
                      <div className="bg-canvas border border-signal p-6">
                        <h3 className="text-base font-semibold text-ink mb-3">Broken Feeds</h3>
                        <div className="space-y-2">
                          {analytics.content.brokenFeeds.slice(0, 5).map((feed, i) => (
                            <div key={i} className="text-sm">
                              <div className="font-medium text-ink">{feed.title}</div>
                              <div className="text-xs text-signal truncate">{feed.error}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Umami Web Analytics */}
                    {analytics.umami && (
                      <div className="bg-canvas border border-edge p-6">
                        <h3 className="text-base font-semibold mb-4">Web Analytics (Umami)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <div className="text-sm text-ink-muted">Page Views</div>
                            <div className="text-2xl font-bold text-ink">{analytics.umami.pageviews}</div>
                          </div>
                          <div>
                            <div className="text-sm text-ink-muted">Unique Visitors</div>
                            <div className="text-2xl font-bold text-ink">{analytics.umami.uniqueVisitors}</div>
                          </div>
                          <div>
                            <div className="text-sm text-ink-muted">Bounce Rate</div>
                            <div className="text-2xl font-bold text-ink">{analytics.umami.bounceRate}%</div>
                          </div>
                          <div>
                            <div className="text-sm text-ink-muted">Avg. Time</div>
                            <div className="text-2xl font-bold text-ink">{Math.round(analytics.umami.averageVisitTime)}s</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium text-ink-secondary mb-2">Top Pages</h4>
                            <div className="space-y-1">
                              {analytics.umami.topPages.slice(0, 5).map((page, i) => (
                                <div key={i} className="flex justify-between text-sm">
                                  <span className="text-ink truncate">{page.path}</span>
                                  <span className="text-ink-muted ml-2">{page.views}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-ink-secondary mb-2">Top Events</h4>
                            <div className="space-y-1">
                              {analytics.umami.topEvents.slice(0, 5).map((event, i) => (
                                <div key={i} className="flex justify-between text-sm">
                                  <span className="text-ink truncate">{event.event}</span>
                                  <span className="text-ink-muted ml-2">{event.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && integrations && (
              <div className="space-y-6 max-w-3xl">
                {/* OpenAI / AI Digests */}
                <div className="bg-canvas border border-edge overflow-hidden">
                  <div className="px-6 py-4 border-b border-edge flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 border border-edge flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-ink" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-ink">OpenAI (AI Digests)</h2>
                        <p className="text-sm text-ink-muted">Powers weekly AI-generated digest summaries</p>
                      </div>
                    </div>
                    <span className={`kicker ${integrations.openai.configured ? 'text-pine' : ''}`}>
                      {integrations.openai.configured ? 'Configured' : 'Not configured'}
                    </span>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-secondary mb-1">API Key</label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input
                            type={showOpenAIKey ? 'text' : 'password'}
                            value={openaiForm.apiKey}
                            onChange={(e) => setOpenaiForm({ ...openaiForm, apiKey: e.target.value })}
                            placeholder={integrations.openai.configured ? '••••••••••••••••' : 'sk-...'}
                            className="field pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-muted"
                          >
                            {showOpenAIKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-ink-muted mt-1">
                        Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">OpenAI</a>
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={openaiForm.digestEnabled}
                          onChange={(e) => setOpenaiForm({ ...openaiForm, digestEnabled: e.target.checked })}
                          className="w-4 h-4 accent-ink"
                        />
                        <span className="text-sm text-ink-secondary">Enable AI Digests</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-secondary mb-1">Digest Schedule (Cron)</label>
                      <input
                        type="text"
                        value={openaiForm.digestSchedule}
                        onChange={(e) => setOpenaiForm({ ...openaiForm, digestSchedule: e.target.value })}
                        placeholder="0 12 * * 0"
                        className="field"
                      />
                      <p className="text-xs text-ink-muted mt-1">Default: Sundays at noon (0 12 * * 0)</p>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={saveOpenAISettings}
                        className="btn btn-ink"
                      >
                        Save Settings
                      </button>
                      {integrations.openai.configured && (
                        <button
                          onClick={testOpenAIKey}
                          disabled={testingOpenAI}
                          className="btn"
                        >
                          {testingOpenAI ? 'Testing...' : 'Test API Key'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Email (Resend) */}
                <div className="bg-canvas border border-edge overflow-hidden">
                  <div className="px-6 py-4 border-b border-edge flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 border border-edge flex items-center justify-center">
                        <Mail className="w-5 h-5 text-ink" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-ink">Email (Resend)</h2>
                        <p className="text-sm text-ink-muted">Send digest emails and notifications to users</p>
                      </div>
                    </div>
                    <span className={`kicker ${integrations.email.configured ? 'text-pine' : ''}`}>
                      {integrations.email.configured ? 'Configured' : 'Not configured'}
                    </span>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-secondary mb-1">Resend API Key</label>
                      <div className="relative">
                        <input
                          type={showResendKey ? 'text' : 'password'}
                          value={emailForm.apiKey}
                          onChange={(e) => setEmailForm({ ...emailForm, apiKey: e.target.value })}
                          placeholder={integrations.email.configured ? '••••••••••••••••' : 're_...'}
                          className="field pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowResendKey(!showResendKey)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-muted"
                        >
                          {showResendKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-ink-muted mt-1">
                        Get your API key from <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Resend</a>
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-ink-secondary mb-1">From Email</label>
                        <input
                          type="email"
                          value={emailForm.fromEmail}
                          onChange={(e) => setEmailForm({ ...emailForm, fromEmail: e.target.value })}
                          placeholder="noreply@yourdomain.com"
                          className="field"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-ink-secondary mb-1">From Name</label>
                        <input
                          type="text"
                          value={emailForm.fromName}
                          onChange={(e) => setEmailForm({ ...emailForm, fromName: e.target.value })}
                          placeholder="OurSS"
                          className="field"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={saveEmailSettings}
                        className="btn btn-ink"
                      >
                        Save Settings
                      </button>
                    </div>
                    {integrations.email.configured && (
                      <div className="pt-4 border-t border-edge">
                        <label className="block text-sm font-medium text-ink-secondary mb-2">Send Test Email</label>
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={testEmailAddress}
                            onChange={(e) => setTestEmailAddress(e.target.value)}
                            placeholder="test@example.com"
                            className="field flex-1"
                          />
                          <button
                            onClick={sendIntegrationTestEmail}
                            className="btn"
                          >
                            <Send className="w-4 h-4" />
                            Send Test
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Analytics (Umami) */}
                <div className="bg-canvas border border-edge overflow-hidden">
                  <div className="px-6 py-4 border-b border-edge flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 border border-edge flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-ink" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-ink">Analytics (Umami)</h2>
                        <p className="text-sm text-ink-muted">Privacy-friendly website analytics</p>
                      </div>
                    </div>
                    <span className={`kicker ${integrations.analytics.configured ? 'text-pine' : ''}`}>
                      {integrations.analytics.configured ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={analyticsForm.enabled}
                          onChange={(e) => setAnalyticsForm({ ...analyticsForm, enabled: e.target.checked })}
                          className="w-4 h-4 accent-ink"
                        />
                        <span className="text-sm text-ink-secondary">Enable Umami Analytics</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-secondary mb-1">Website ID</label>
                      <input
                        type="text"
                        value={analyticsForm.websiteId}
                        onChange={(e) => setAnalyticsForm({ ...analyticsForm, websiteId: e.target.value })}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        className="field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-secondary mb-1">Script URL</label>
                      <input
                        type="text"
                        value={analyticsForm.scriptUrl}
                        onChange={(e) => setAnalyticsForm({ ...analyticsForm, scriptUrl: e.target.value })}
                        placeholder="https://analytics.yourdomain.com/script.js"
                        className="field"
                      />
                      <p className="text-xs text-ink-muted mt-1">
                        Self-host Umami or use <a href="https://umami.is" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Umami Cloud</a>
                      </p>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={saveAnalyticsSettings}
                        className="btn btn-ink"
                      >
                        Save Settings
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Licenses Tab */}
            {activeTab === 'licenses' && (
              <div className="space-y-6">
                {/* Issue New License */}
                <div className="bg-canvas border border-edge p-6">
                  <h2 className="text-lg font-semibold mb-4">Issue New License</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="email"
                      placeholder="Customer email"
                      value={newLicenseEmail}
                      onChange={(e) => setNewLicenseEmail(e.target.value)}
                      className="field w-auto"
                    />
                    <input
                      type="text"
                      placeholder="Customer name (optional)"
                      value={newLicenseName}
                      onChange={(e) => setNewLicenseName(e.target.value)}
                      className="field w-auto"
                    />
                    <button
                      onClick={issueLicense}
                      className="btn btn-ink"
                    >
                      Issue License
                    </button>
                  </div>
                </div>

                {/* Licenses List */}
                <div className="bg-canvas border border-edge overflow-hidden">
                  <div className="px-6 py-4 border-b border-edge">
                    <h2 className="text-lg font-semibold">All Licenses ({licenses.length})</h2>
                  </div>
                  {licenses.length === 0 ? (
                    <div className="p-6 text-center text-ink-muted">
                      No licenses issued yet
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-edge-subtle">
                        <thead className="bg-surface-sunken">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">License Key</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Activation</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">Last Seen</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-ink-muted uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-canvas divide-y divide-edge-subtle">
                          {licenses.map((license) => (
                            <tr key={license.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <code className="text-sm font-mono text-ink">{license.licenseKey}</code>
                                  <button
                                    onClick={() => copyLicenseKey(license.licenseKey)}
                                    className="p-1 hover:bg-surface-sunken"
                                    title="Copy license key"
                                  >
                                    {copiedKey === license.licenseKey ? (
                                      <Check className="w-4 h-4 text-pine" />
                                    ) : (
                                      <Copy className="w-4 h-4 text-ink-muted" />
                                    )}
                                  </button>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-ink">{license.customerEmail}</div>
                                {license.customerName && (
                                  <div className="text-xs text-ink-muted">{license.customerName}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`kicker ${
                                  license.status === 'active' ? 'text-pine' :
                                  license.status === 'revoked' ? 'text-signal' :
                                  'text-signal'
                                }`}>
                                  {license.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                                {license.machineId ? (
                                  <div>
                                    <span className="kicker text-ink">
                                      Activated
                                    </span>
                                    {license.instanceUrl && (
                                      <div className="text-xs text-ink-muted mt-1 truncate max-w-xs">
                                        {license.instanceUrl}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-ink-muted">Not activated</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                                {license.lastSeenAt ? (
                                  <div>
                                    <div>{new Date(license.lastSeenAt).toLocaleDateString()}</div>
                                    <div className="text-xs text-ink-muted">
                                      {new Date(license.lastSeenAt).toLocaleTimeString()}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-ink-muted">Never</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                {license.machineId && (
                                  <button
                                    onClick={() => resetActivation(license.id)}
                                    className="kicker hover:text-ink"
                                    title="Reset activation"
                                  >
                                    <RotateCcw className="w-4 h-4 inline" />
                                  </button>
                                )}
                                {license.status === 'active' ? (
                                  <button
                                    onClick={() => revokeLicense(license.id)}
                                    className="kicker text-signal hover:text-ink"
                                    title="Revoke license"
                                  >
                                    <Trash2 className="w-4 h-4 inline" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => reinstateLicense(license.id)}
                                    className="kicker text-pine hover:text-ink"
                                    title="Reinstate license"
                                  >
                                    <RefreshCw className="w-4 h-4 inline" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Announcements Tab */}
            {activeTab === 'announcements' && (
              <div className="space-y-6 max-w-4xl">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-ink">Announcements</h2>
                    <p className="text-sm text-ink-muted mt-1">Create and manage announcements for your users</p>
                  </div>
                  <button
                    onClick={() => {
                      resetAnnouncementForm();
                      setShowAnnouncementForm(true);
                    }}
                    className="btn btn-ink"
                  >
                    <Plus className="w-4 h-4" />
                    New Announcement
                  </button>
                </div>

                {/* Form Modal */}
                {showAnnouncementForm && (
                  <div className="bg-canvas border border-edge p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold">
                        {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
                      </h3>
                      <button
                        onClick={resetAnnouncementForm}
                        className="p-1 hover:bg-surface-sunken"
                      >
                        <X className="w-5 h-5 text-ink-muted" />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-ink-secondary mb-1">Title</label>
                        <input
                          type="text"
                          value={announcementForm.title}
                          onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                          placeholder="Announcement title"
                          className="field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-ink-secondary mb-1">Content (Markdown)</label>
                        <textarea
                          value={announcementForm.content}
                          onChange={(e) => setAnnouncementForm({ ...announcementForm, content: e.target.value })}
                          placeholder="Write your announcement content in Markdown..."
                          rows={6}
                          className="field field-mono"
                        />
                        <p className="text-xs text-ink-muted mt-1">Supports Markdown: **bold**, *italic*, [links](url), etc.</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-ink-secondary mb-1">Type</label>
                          <select
                            value={announcementForm.type}
                            onChange={(e) => setAnnouncementForm({ ...announcementForm, type: e.target.value as 'modal' | 'notification' })}
                            className="field"
                          >
                            <option value="modal">Modal (popup)</option>
                            <option value="notification">Notification</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-ink-secondary mb-1">Priority</label>
                          <input
                            type="number"
                            value={announcementForm.priority}
                            onChange={(e) => setAnnouncementForm({ ...announcementForm, priority: parseInt(e.target.value) || 0 })}
                            className="field"
                          />
                          <p className="text-xs text-ink-muted mt-1">Higher = shown first</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-ink-secondary mb-1">Expires On (optional)</label>
                          <input
                            type="date"
                            value={announcementForm.expiresAt}
                            onChange={(e) => setAnnouncementForm({ ...announcementForm, expiresAt: e.target.value })}
                            className="field"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-ink-secondary mb-1">Target Audience</label>
                          <select
                            value={announcementForm.targetRole}
                            onChange={(e) => setAnnouncementForm({ ...announcementForm, targetRole: e.target.value as '' | 'user' | 'admin' })}
                            className="field"
                          >
                            <option value="">All users</option>
                            <option value="user">Regular users only</option>
                            <option value="admin">Admins only</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={announcementForm.isActive}
                            onChange={(e) => setAnnouncementForm({ ...announcementForm, isActive: e.target.checked })}
                            className="w-4 h-4 accent-ink"
                          />
                          <span className="text-sm text-ink-secondary">Active (visible to users)</span>
                        </label>
                      </div>

                      <div className="flex gap-2 pt-4">
                        <button
                          onClick={saveAnnouncement}
                          disabled={!announcementForm.title || !announcementForm.content}
                          className="btn btn-ink"
                        >
                          {editingAnnouncement ? 'Update Announcement' : 'Create Announcement'}
                        </button>
                        <button
                          onClick={resetAnnouncementForm}
                          className="btn"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Announcements List */}
                <div className="bg-canvas border border-edge overflow-hidden">
                  {announcementsList.length === 0 ? (
                    <div className="p-8 text-center text-ink-muted">
                      <Megaphone className="w-12 h-12 mx-auto mb-3 text-ink-faint" />
                      <p>No announcements yet</p>
                      <p className="text-sm">Create your first announcement to notify users</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-edge-subtle">
                      {announcementsList.map((announcement) => {
                        const isExpired = announcement.expiresAt && new Date(announcement.expiresAt) < new Date();
                        return (
                          <div key={announcement.id} className="p-4 hover:bg-surface-sunken">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="text-sm font-medium text-ink truncate">{announcement.title}</h4>
                                  <span className="kicker text-ink">
                                    {announcement.type}
                                  </span>
                                  {announcement.isActive && !isExpired ? (
                                    <span className="kicker text-pine">
                                      Active
                                    </span>
                                  ) : isExpired ? (
                                    <span className="kicker">
                                      Expired
                                    </span>
                                  ) : (
                                    <span className="kicker text-signal">
                                      Inactive
                                    </span>
                                  )}
                                  {announcement.targetRole && (
                                    <span className="kicker">
                                      {announcement.targetRole}s only
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-ink-muted line-clamp-2 mb-2">{announcement.content}</p>
                                <div className="flex items-center gap-4 text-xs text-ink-muted">
                                  <span>Priority: {announcement.priority}</span>
                                  <span>Created: {new Date(announcement.createdAt).toLocaleDateString()}</span>
                                  {announcement.expiresAt && (
                                    <span>Expires: {new Date(announcement.expiresAt).toLocaleDateString()}</span>
                                  )}
                                  {announcement._count && (
                                    <span>{announcement._count.dismissals} dismissed</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <button
                                  onClick={() => toggleAnnouncementActive(announcement)}
                                  className={`p-2 hover:bg-surface-sunken ${
                                    announcement.isActive ? 'text-pine' : 'text-ink-faint'
                                  }`}
                                  title={announcement.isActive ? 'Deactivate' : 'Activate'}
                                >
                                  {announcement.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => startEditAnnouncement(announcement)}
                                  className="p-2 hover:bg-surface-sunken text-ink-muted hover:text-ink"
                                  title="Edit"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deleteAnnouncement(announcement.id)}
                                  className="p-2 hover:bg-surface-sunken text-signal"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Configuration Tab */}
            {activeTab === 'config' && config && (
              <div className="max-w-2xl">
                <div className="bg-canvas border border-edge p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold">Instance Configuration</h2>
                    {!editingConfig && (
                      <button
                        onClick={() => setEditingConfig(true)}
                        className="btn btn-ink"
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-secondary mb-1">Instance Name</label>
                      {editingConfig ? (
                        <input
                          type="text"
                          value={configForm.name}
                          onChange={(e) => setConfigForm({ ...configForm, name: e.target.value })}
                          className="field"
                        />
                      ) : (
                        <div className="text-ink">{config.name}</div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-secondary mb-1">Description</label>
                      {editingConfig ? (
                        <textarea
                          value={configForm.description}
                          onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
                          rows={3}
                          className="field"
                        />
                      ) : (
                        <div className="text-ink">{config.description || 'No description'}</div>
                      )}
                    </div>

                    <div>
                      <label className="flex items-center space-x-2">
                        {editingConfig ? (
                          <input
                            type="checkbox"
                            checked={configForm.allowRegistration}
                            onChange={(e) => setConfigForm({ ...configForm, allowRegistration: e.target.checked })}
                            className="w-4 h-4 accent-ink"
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={config.allowRegistration}
                            disabled
                            className="w-4 h-4 accent-ink"
                          />
                        )}
                        <span className="text-sm font-medium text-ink-secondary">Allow Registration</span>
                      </label>
                      <p className="text-xs text-ink-muted mt-1 ml-6">Allow new users to register with invite codes</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-secondary mb-1">Max Users (0 = unlimited)</label>
                      {editingConfig ? (
                        <input
                          type="number"
                          min="0"
                          value={configForm.maxUsers}
                          onChange={(e) => setConfigForm({ ...configForm, maxUsers: parseInt(e.target.value) || 0 })}
                          className="field"
                        />
                      ) : (
                        <div className="text-ink">{config.maxUsers === 0 ? 'Unlimited' : config.maxUsers}</div>
                      )}
                    </div>

                    {editingConfig && (
                      <div className="flex gap-2 pt-4">
                        <button
                          onClick={saveConfig}
                          className="btn btn-ink"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            setEditingConfig(false);
                            setConfigForm({
                              name: config.name,
                              description: config.description || '',
                              allowRegistration: config.allowRegistration,
                              maxUsers: config.maxUsers,
                            });
                          }}
                          className="btn"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
